import { useState } from "react";

import CampaignsEmptyPlaceholder from "common/components/CampaignsEmptyPlaceholder";
import { LeadPreview } from "common/components/leads";
import MetricSelector from "common/components/MetricSelector";
import RecentLeads from "common/components/RecentLeads";
import RestrictedComponent from "common/components/RestrictedComponent";
import StatisticsGraph from "common/components/StatisticsGraph";
import TimeframeSelector from "common/components/TimeframeSelector";
import { useSelectedWorkspaceContext } from "common/context/SelectedWorkspaceContext";
import useCampaigns from "common/datahooks/useCampaigns";
import useMetrics from "common/datahooks/useMetrics";
import { formatDate } from "common/helpers/utils";
import useSelectedMetric from "common/hooks/useSelectedMetric";
import useSelectedTimeframe from "common/hooks/useSelectedTimeframe";
import {
  ContentContainer,
  ContentHeader,
  ContentScroll,
  PageTitle,
} from "common/layouts/WorkspaceLayout";

import WorkspaceAccountSelector from "../../../common/components/WorkspaceAccountSelector";
import ExportReport from "./ExportReport";

const timeframeLocalStorageKey = "home-timeframe";

function Home() {
  const workspace = useSelectedWorkspaceContext();
  const [selectedAccountIds, setSelectedAccountIds] = useState<string[]>([]);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [previewLeadId, setPreviewLeadId] = useState<string>();

  const { selectedMetric, setSelectedMetric } = useSelectedMetric({
    localStorageKey: "selectedMetric",
  });
  const { selectedTimeframe, selectedTimeframeOption, setSelectedTimeframe } =
    useSelectedTimeframe(
      JSON.parse(localStorage.getItem(timeframeLocalStorageKey)),
    );

  const {
    metrics,
    metricsSum,
    isLoadingMetrics,
    refetchMetrics,
    metricsError,
  } = useMetrics({
    selectedTimeframe,
    accountIds: selectedAccountIds,
  });

  const { campaigns, isLoadingCampaigns } = useCampaigns();

  const isLoading = isLoadingCampaigns || isLoadingMetrics;

  let campaignsEmptyPlaceholder = null;
  if (!isLoading && (!campaigns || !campaigns.length)) {
    campaignsEmptyPlaceholder = <CampaignsEmptyPlaceholder />;
  }

  return (
    <div className="flex h-full gap-x-2">
      <ContentContainer>
        <ContentHeader>
          <PageTitle />
          <ExportReport initialTimeframe={selectedTimeframe} />
        </ContentHeader>
        <ContentScroll className="p-4 gap-y-4">
          <div className="flex justify-between items-center">
            <div className="flex flex-col gap-y-0.5 max-lg:hidden">
              <h2 className="text-lg-bold">Overview</h2>
              <p className="text-sm-medium text-black-500">
                {typeof selectedTimeframe === "string"
                  ? selectedTimeframeOption.label
                  : `${formatDate(selectedTimeframe.from)} - ${formatDate(selectedTimeframe.to)}`}{" "}
                statistics
              </p>
            </div>
            <div className="flex gap-x-2.5 max-lg:w-full">
              <WorkspaceAccountSelector
                selection={selectedAccountIds}
                setSelection={setSelectedAccountIds}
                className="max-lg:flex-1 lg:w-44"
              />
              <TimeframeSelector
                shouldOffsetTimezone={false}
                selectedTimeframe={selectedTimeframe}
                selectTimeframe={(selection) => {
                  setSelectedTimeframe(selection);
                  localStorage.setItem(
                    timeframeLocalStorageKey,
                    JSON.stringify(selection),
                  );
                }}
                className="max-lg:flex-1 lg:w-44"
              />
            </div>
          </div>
          <div className="flex flex-col gap-y-2.5">
            <MetricSelector
              metricsSum={metricsSum}
              selectedMetric={selectedMetric}
              setSelectedMetric={setSelectedMetric}
              isLoading={isLoadingMetrics}
            />

            {campaignsEmptyPlaceholder || (
              <StatisticsGraph
                selectedMetric={selectedMetric}
                metrics={metrics}
                metricsSum={metricsSum}
                refetchMetrics={refetchMetrics}
                requestError={metricsError}
                isLoading={isLoading}
                isHourly={
                  selectedTimeframeOption.getParameters(workspace)
                    .bucketSize === "1 hour"
                }
              />
            )}
          </div>
          <RestrictedComponent visibleForPermissions={["leads_full"]}>
            <section className="mt-4">
              <h3 className="mb-1 text-xl-bold">Recent leads</h3>
              <p className="text-black-500 text-sm mb-4">
                The latest leads from your outreach campaigns
              </p>
              <RecentLeads
                accountIds={selectedAccountIds}
                onSelectLead={(leadId) => {
                  setIsPreviewOpen(true);
                  setPreviewLeadId(leadId);
                }}
              />
            </section>
          </RestrictedComponent>
        </ContentScroll>
      </ContentContainer>
      <LeadPreview
        leadId={previewLeadId}
        isOpen={isPreviewOpen}
        onClose={() => setIsPreviewOpen(false)}
      />
    </div>
  );
}

export default Home;
